/**
 * Balance formatting and calculation utilities
 */

/**
 * Format a balance value for display
 */
export function formatBalance(value, decimals = 2) {
  if (value === undefined || value === null) return '0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0.00';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Get exchange balances from the balances response
 */
export function getExchangeBalances(balances, exchangeId) {
  if (!balances?.exchanges?.[exchangeId]) return null;
  return balances.exchanges[exchangeId];
}

/**
 * Get treasury balances from the balances response
 */
export function getTreasuryBalances(balances) {
  return balances?.treasury || {};
}

/**
 * Check if a value is a treasury wallet (starts with "treasury:")
 */
export function isTreasuryWallet(value) {
  return value && value.startsWith('treasury:');
}

/**
 * Extract wallet address from treasury wallet value
 */
export function getTreasuryWalletAddress(value) {
  if (!isTreasuryWallet(value)) return null;
  return value.replace('treasury:', '');
}

/**
 * Get balances for a specific treasury wallet and chain
 */
export function getTreasuryWalletBalances(balances, walletAddress, chain = null) {
  if (!balances?.treasury || !walletAddress) return null;

  const normalizedAddress = walletAddress.toLowerCase();
  const walletBalances = {};

  Object.entries(balances.treasury).forEach(([key, bal]) => {
    const balanceAddress = bal.address?.toLowerCase();
    if (!balanceAddress || balanceAddress !== normalizedAddress) {
      return;
    }

    const keyParts = key.split('_');
    if (keyParts.length < 3) return;

    const asset = keyParts[0].toUpperCase();
    const balanceChain = keyParts[1]?.toLowerCase();

    if (chain && balanceChain !== chain.toLowerCase()) {
      return;
    }

    if (!walletBalances[asset]) {
      walletBalances[asset] = {
        available: 0,
        locked: 0,
      };
    }

    walletBalances[asset].available += parseFloat(bal.available || 0);
    walletBalances[asset].locked += parseFloat(bal.locked || 0);
  });

  return walletBalances;
}

/**
 * Calculate total balance for an exchange
 */
export function calculateExchangeTotal(exchangeBalances) {
  if (!exchangeBalances) return 0;

  let total = 0;
  Object.values(exchangeBalances).forEach((balance) => {
    const equity = parseFloat(balance.equity || balance.available || '0');
    total += equity;
  });

  return total;
}

/**
 * Calculate total balance across all exchanges
 */
export function calculateTotalBalance(balances) {
  if (!balances?.exchanges) return 0;

  let total = 0;
  Object.values(balances.exchanges).forEach((exchangeBalances) => {
    total += calculateExchangeTotal(exchangeBalances);
  });

  return total;
}

/**
 * Shorten an address for display
 */
export function shortenAddress(addr, start = 6, end = 4) {
  if (!addr || addr.length < start + end + 3) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
}
